import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { StatusDot } from '../common/StatusDot';

interface SiteAccessChecklistProps {
  selectedSiteIds: string[];
  onChange: (ids: string[]) => void;
}

interface SiteOption {
  site_id: string;
  name: string;
  status: 'online' | 'offline' | 'degraded' | 'maintenance';
}

export function SiteAccessChecklist({ selectedSiteIds, onChange }: SiteAccessChecklistProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['all-sites-list'],
    queryFn: async () => {
      const res = await api.get('/v1/sites?limit=200');
      return res.data.data.sites as SiteOption[];
    },
    staleTime: 60000,
  });

  const sites = data || [];
  const allSelected = sites.length > 0 && selectedSiteIds.length === sites.length;

  const toggleAll = () => {
    onChange(allSelected ? [] : sites.map((s) => s.site_id));
  };

  const toggle = (id: string) => {
    onChange(
      selectedSiteIds.includes(id)
        ? selectedSiteIds.filter((s) => s !== id)
        : [...selectedSiteIds, id]
    );
  };

  if (isLoading) {
    return <div className="text-sm text-ems-text-muted py-2">Loading sites...</div>;
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={toggleAll}
        className="text-xs text-ems-green hover:underline"
      >
        {allSelected ? 'Deselect All' : 'Select All'}
      </button>
      <div className="max-h-48 overflow-y-auto space-y-1">
        {sites.map((site) => (
          <label key={site.site_id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-ems-bg-card-hover cursor-pointer">
            <input
              type="checkbox"
              checked={selectedSiteIds.includes(site.site_id)}
              onChange={() => toggle(site.site_id)}
              className="rounded border-ems-border text-ems-green focus:ring-ems-green/20"
            />
            <StatusDot status={site.status === 'maintenance' ? 'degraded' : (site.status as any)} size="sm" />
            <span className="text-sm text-ems-text-primary">{site.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
